import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/routes/routes';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { DownloadVoltAppBanner } from '@/ui/components';
import { Button } from '@/ui/components';
import { Icon } from '@/ui/icons/Icon';
import { formatPhoneForDisplay } from '@/lib/phoneUtils';
import { getCopyrightWithVersion } from '@/lib/version';
import styles from './SideMenu.module.scss';
import { useUser } from '@/features/user/hooks/useUser';
import { useFreshchat } from '@/features/support';
import { CookiePreferences } from '@/features/cookie/components';
import { useCookie } from '@/providers/Cookie';

interface SideMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SideMenu({ isOpen, onClose }: SideMenuProps) {
  const { user } = useUser();
  const menuRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { showWidget } = useFreshchat();
  const {
    acceptAll,
    acceptPreferences,
    isCookiePreferencesOpen,
    setIsCookiePreferencesOpen,
  } = useCookie();

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Focus the menu when it opens
      if (menuRef.current) {
        menuRef.current.focus();
      }
    } else {
      // Restore focus when menu closes
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isCookiePreferencesOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isCookiePreferencesOpen]);

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && !isCookiePreferencesOpen) {
      onClose();
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    onClose();
    navigate(routes.public.signup, { replace: true });
  };

  // Handle cookie preferences
  const handleCookiePreferences = () => {
    setIsCookiePreferencesOpen(true);
  };

  if (!isOpen) {
    return (
      <CookiePreferences
        open={isCookiePreferencesOpen}
        onOpenChange={setIsCookiePreferencesOpen}
        onAcceptAll={acceptAll}
        onAcceptSettings={acceptPreferences}
      />
    );
  }

  const menuContent = (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div
        ref={menuRef}
        className={styles.menu}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        tabIndex={-1}
      >
        <div className={styles.menuBody}>
          {/* Desktop Actions Section */}
          <div className={styles.desktopActions}>
            <Button
              trackingId="side_menu_help"
              variant="tonal"
              size="small"
              className={styles.helpButton}
              onClick={showWidget}
              rightIcon={
                <Icon name="question-circle" className={styles.helpIcon} />
              }
            >
              <span className={styles.helpText}>Ajutor</span>
            </Button>
            <Button
              trackingId="close_side_menu"
              variant="text"
              onClick={onClose}
              size="xsmall"
              rightIcon={<Icon name="menu" className={styles.menuIcon} />}
              className={styles.menuButton}
              iconOnly={true}
            />
          </div>

          {user && (
            <div className={styles.menuHeader}>
              <div className={styles.phonePill}>
                <p className={styles.phoneNumber}>
                  {user?.phone ? formatPhoneForDisplay(user.phone) : ''}
                </p>
                <Button
                  trackingId="side_menu_logout"
                  variant="text"
                  size="xsmall"
                  iconOnly
                  aria-label="Deconectare"
                  onClick={handleLogout}
                  className={styles.logoutButton}
                  rightIcon={<Icon name="log-out" size={20} />}
                />
              </div>
            </div>
          )}
        </div>

        <div className={styles.menuFooter}>
          <div className={styles.links}>
            <Button
              trackingId="side_menu_cookie_preferences"
              variant="text"
              size="xsmall"
              className={styles.cookiePreferencesButton}
              contentClassName={styles.cookiePreferencesText}
              onClick={handleCookiePreferences}
            >
              Preferințe cookies
            </Button>
            <p className={styles.copyrightText}>{getCopyrightWithVersion()}</p>
          </div>

          <DownloadVoltAppBanner className={styles.downloadBanner} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(menuContent, document.body)}
      <CookiePreferences
        open={isCookiePreferencesOpen}
        onOpenChange={setIsCookiePreferencesOpen}
        onAcceptAll={acceptAll}
        onAcceptSettings={acceptPreferences}
      />
    </>
  );
}
